import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Loader2, Sparkles, FileDown, RefreshCcw, IndianRupee, Clock, Repeat, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/ist";
import { MiniMarkdown } from "../../lib/miniMarkdown";

const SRC_COLORS = { payment_link: "#3b82f6", api: "#06b6d4", sm888: "#8b5cf6", manual: "#f59e0b", platform: "#10b981" };
const SRC_LABEL = { payment_link: "Payment Link", api: "Optivize API", sm888: "SM888", manual: "Manual", platform: "Platform" };
const GW_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function CcyTotals({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <div className="text-2xl font-extrabold text-white">₹0</div>;
  return (
    <div>
      {entries.map(([ccy, v]) => (
        <div key={ccy} className="text-2xl font-extrabold text-white leading-tight">
          {ccy === "INR" ? inr(v.total) : `${ccy} ${v.total.toLocaleString()}`}
          <span className="text-xs text-slate-500 font-normal ml-1.5">{v.count} txns</span>
        </div>
      ))}
    </div>
  );
}

function SummaryCards({ ov }) {
  const cards = [
    { label: "Collected Today (24h)", data: ov.today, icon: IndianRupee, color: "text-emerald-400" },
    { label: "This Week (7d)", data: ov.week, icon: IndianRupee, color: "text-blue-400" },
    { label: "This Month (30d)", data: ov.month, icon: IndianRupee, color: "text-violet-400" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="acct-summary-cards">
      {cards.map(c => (
        <div key={c.label} className="card p-4">
          <div className={`text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5`}><c.icon size={12} className={c.color}/> {c.label}</div>
          <div className="mt-2"><CcyTotals data={c.data}/></div>
        </div>
      ))}
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5"><Clock size={12} className="text-amber-400"/> Pending Payments</div>
        <div className="mt-2 text-2xl font-extrabold text-amber-300">{inr(ov.pending?.pending_inr_total)}</div>
        <div className="text-xs text-slate-500">{ov.pending?.payment_links} pending links · {ov.pending?.unpaid_invoices} unpaid invoices</div>
      </div>
    </div>
  );
}

function Charts({ series, ov }) {
  const gw = Object.entries(ov.by_gateway_30d_inr || {}).map(([name, v]) => ({ name: name === "manual" ? "Manual" : name, value: v.total }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
      <div className="card p-4 lg:col-span-2" data-testid="acct-revenue-chart">
        <div className="text-xs font-bold text-slate-300 mb-2">Daily collections — last 30 days (INR, by source)</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={series} margin={{ left: 0, right: 8, top: 4 }}>
            <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={d => d.slice(5)} interval={4}/>
            <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${v/1000}k` : v}/>
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [inr(v), SRC_LABEL[n] || n]}/>
            <Legend formatter={n => <span style={{ fontSize: 11, color: "#94a3b8" }}>{SRC_LABEL[n] || n}</span>}/>
            {Object.keys(SRC_COLORS).map(s => <Bar key={s} dataKey={s} stackId="rev" fill={SRC_COLORS[s]} radius={[2,2,0,0]}/>)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card p-4" data-testid="acct-gateway-pie">
        <div className="text-xs font-bold text-slate-300 mb-2">By payment gateway (30d, INR)</div>
        {gw.length === 0 ? <div className="text-slate-500 text-sm py-10 text-center">No data</div> : (
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={gw} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {gw.map((_, i) => <Cell key={i} fill={GW_COLORS[i % GW_COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }} formatter={v => inr(v)}/>
              <Legend formatter={n => <span style={{ fontSize: 11, color: "#94a3b8" }}>{n}</span>}/>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function AIInsights() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounting/ai-insights");
      setReport(data.report);
    } catch { toast.error("AI analysis failed"); } finally { setBusy(false); }
  };
  return (
    <div className="card p-4 mt-4" data-testid="acct-ai-insights">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-slate-300 flex items-center gap-1.5"><Sparkles size={13} className="text-emerald-400"/> Accounting AI Agent — analysis & recommendations</div>
        <button onClick={run} disabled={busy} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="acct-ai-run">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} {busy ? "Analysing…" : "Generate AI report"}
        </button>
      </div>
      {report && <div className="mt-3 border-t border-slate-800 pt-3"><MiniMarkdown content={report}/></div>}
    </div>
  );
}

function invoiceAction(r) {
  if (r.invoice_pdf) return <a href={r.invoice_pdf} download title="Download invoice PDF" className="text-emerald-400 hover:text-emerald-300 inline-block p-1" data-testid={`acct-inv-${r.id.slice(0,6)}`}><FileDown size={14}/></a>;
  if (r.invoice_id_auth) {
    const dl = async () => {
      try {
        const resp = await api.get(`/admin/invoices/${r.invoice_id_auth}/pdf?copy=customer`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement("a"); a.href = url; a.download = `${r.invoice_number || "invoice"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } catch { toast.error("PDF download failed"); }
    };
    return <button onClick={dl} title="Download invoice PDF" className="text-emerald-400 hover:text-emerald-300 p-1" data-testid={`acct-inv-${r.id.slice(0,6)}`}><FileDown size={14}/></button>;
  }
  return null;
}

function RecordsTable() {
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState("");
  const [gw, setGw] = useState("");
  const [q, setQ] = useState("");
  const [days, setDays] = useState(90);
  const load = () => {
    const p = new URLSearchParams();
    if (source) p.set("source", source);
    if (gw) p.set("gateway", gw);
    if (q) p.set("q", q);
    p.set("days", days);
    api.get(`/admin/accounting/records?${p}`).then(r=>setRows(r.data.rows)).catch(()=>setRows([]));
  };
  useEffect(load, [source, gw, days]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={source} onChange={e=>setSource(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300" data-testid="acct-filter-source">
          <option value="">All sources</option>
          {Object.entries(SRC_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={gw} onChange={e=>setGw(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300" data-testid="acct-filter-gateway">
          <option value="">All gateways</option>
          <option value="razorpay">Razorpay</option>
          <option value="payglocal">PayGlocal</option>
          <option value="manual">Manual</option>
        </select>
        <select value={days} onChange={e=>setDays(Number(e.target.value))} className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300" data-testid="acct-filter-days">
          {[7,30,90,180,365].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()} placeholder="Search customer / product / invoice…"
          className="flex-1 min-w-[180px] bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600" data-testid="acct-search"/>
        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-slate-700 text-slate-200 text-xs font-bold inline-flex items-center gap-1" data-testid="acct-records-refresh"><RefreshCcw size={12}/> Apply</button>
      </div>
      <div className="card overflow-x-auto" data-testid="acct-records-table">
        {!rows ? <Loader2 className="animate-spin text-blue-400 mx-auto my-8"/> : rows.length === 0 ? <p className="text-slate-500 text-sm p-6 text-center">No records.</p> : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr><th className="py-2.5 px-3 text-left">Date (IST)</th><th className="text-left px-2">Customer</th><th className="text-left px-2">Product</th><th className="text-left px-2">Source</th><th className="text-left px-2">Gateway</th><th className="text-right px-2">Amount</th><th className="text-left px-2">Invoice #</th><th className="text-left px-2">Txn ID</th><th className="text-right px-3">PDF</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={`${r.source}-${r.id}`} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-slate-400 text-xs whitespace-nowrap">{fmtDateTime(r.date)}</td>
                  <td className="px-2 text-xs"><div className="text-slate-200 font-semibold">{r.customer_name || "—"}</div><div className="text-slate-500">{r.customer_email}</div></td>
                  <td className="px-2 text-slate-300 text-xs max-w-[150px] truncate">{r.product}</td>
                  <td className="px-2"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${SRC_COLORS[r.source]}22`, color: SRC_COLORS[r.source] }}>{SRC_LABEL[r.source]}</span></td>
                  <td className="px-2 text-xs text-slate-400">{r.gateway || "—"}</td>
                  <td className="px-2 text-right text-slate-100 font-semibold whitespace-nowrap">{r.currency === "INR" ? inr(r.amount) : `${r.currency} ${r.amount}`}</td>
                  <td className="px-2 font-mono text-[11px] text-cyan-300">{r.invoice_number || "—"}</td>
                  <td className="px-2 font-mono text-[11px] text-slate-500 max-w-[110px] truncate">{r.transaction_id || "—"}</td>
                  <td className="px-3 text-right">{invoiceAction(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PendingTable() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/accounting/pending").then(r=>setRows(r.data.rows)).catch(()=>setRows([])); }, []);
  if (!rows) return <Loader2 className="animate-spin text-amber-400 mx-auto my-8"/>;
  return (
    <div className="card overflow-x-auto mt-4" data-testid="acct-pending-table">
      {rows.length === 0 ? <p className="text-slate-500 text-sm p-6 text-center">No pending payments 🎉</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
            <tr><th className="py-2.5 px-3 text-left">Created</th><th className="text-left px-2">Type</th><th className="text-left px-2">Product</th><th className="text-left px-2">Customer</th><th className="text-right px-2">Amount</th><th className="text-left px-3">Expires</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-800">
                <td className="py-2 px-3 text-slate-400 text-xs whitespace-nowrap">{fmtDateTime(r.date)}</td>
                <td className="px-2 text-xs text-slate-300">{SRC_LABEL[r.source] || r.type}</td>
                <td className="px-2 text-slate-200 text-xs max-w-[180px] truncate">{r.product}</td>
                <td className="px-2 text-xs text-slate-400">{r.customer_name || r.customer_email || "—"}</td>
                <td className="px-2 text-right text-amber-300 font-semibold">{r.currency === "INR" ? inr(r.amount) : `${r.currency} ${r.amount}`}</td>
                <td className="px-3 text-xs text-slate-500">{r.expires_at ? fmtDateTime(r.expires_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RenewalsTable() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/accounting/renewals").then(r=>setRows(r.data.rows)).catch(()=>setRows([])); }, []);
  if (!rows) return <Loader2 className="animate-spin text-violet-400 mx-auto my-8"/>;
  return (
    <div className="card overflow-x-auto mt-4" data-testid="acct-renewals-table">
      {rows.length === 0 ? <p className="text-slate-500 text-sm p-6 text-center">No subscription renewals yet.</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
            <tr><th className="py-2.5 px-3 text-left">Product</th><th className="text-left px-2">Customer</th><th className="text-right px-2">Amount</th><th className="text-left px-2">Renewals paid</th><th className="text-left px-2">Status</th><th className="text-left px-3">Last update</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-800">
                <td className="py-2 px-3 text-slate-200 text-xs font-semibold">{r.product}</td>
                <td className="px-2 text-xs text-slate-400">{r.customer_name || r.customer_email || "—"}</td>
                <td className="px-2 text-right text-slate-100">{inr(r.amount_inr)}</td>
                <td className="px-2 text-xs text-slate-300">{r.paid_count ?? 0}{r.total_count ? ` / ${r.total_count}` : ""}</td>
                <td className="px-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-emerald-600/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{r.status}</span></td>
                <td className="px-3 text-xs text-slate-500">{r.updated_at ? fmtDateTime(r.updated_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminAccounting() {
  const [ov, setOv] = useState(null);
  const [series, setSeries] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    api.get("/admin/accounting/overview").then(r=>setOv(r.data)).catch(()=>toast.error("Failed to load accounting overview"));
    api.get("/admin/accounting/series?days=30").then(r=>setSeries(r.data.rows)).catch(()=>{});
  }, []);
  if (!ov) return <Loader2 className="animate-spin text-emerald-400 mx-auto my-16"/>;
  const TABS = [["overview","Overview",Sparkles],["records","All Records",ListChecks],["pending","Pending",Clock],["renewals","Renewals",Repeat]];
  return (
    <div data-testid="admin-accounting">
      <h2 className="text-lg font-extrabold text-white" style={{ fontFamily: "Outfit" }}>Accounting</h2>
      <p className="text-slate-500 text-xs mb-4">Unified collections across Payment Gateway, Manual and Optivize API — powered by the Accounting AI Agent.</p>
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {TABS.map(([id,label,Icon]) => (
          <button key={id} onClick={()=>setTab(id)} className={`px-3 py-2 rounded-lg text-xs font-bold border inline-flex items-center gap-1.5 ${tab===id ? "bg-emerald-600 border-emerald-600 text-white" : "bg-slate-800/60 border-slate-700 text-slate-400"}`} data-testid={`acct-tab-${id}`}><Icon size={13}/> {label}</button>
        ))}
      </div>
      {tab === "overview" && (<><SummaryCards ov={ov}/>{series && <Charts series={series} ov={ov}/>}<AIInsights/></>)}
      {tab === "records" && <RecordsTable/>}
      {tab === "pending" && <PendingTable/>}
      {tab === "renewals" && <RenewalsTable/>}
    </div>
  );
}
